import React, { useState, useEffect } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../firebase/config';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Loader, X } from 'lucide-react';
import toast from 'react-hot-toast';
import checkAdminAccess from '../../utils/checkAdminAccess';

export default function CreateInterview() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [generatingQuestions, setGeneratingQuestions] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [showQuestions, setShowQuestions] = useState(false);
  const [formData, setFormData] = useState({
    positionTitle: '',
    department: '',
    experienceLevel: '',
    jobDescription: '',
    requiredSkills: '',
    technicalRequirements: '',
    softSkillsRequired: '',
    additionalNotes: ''
  });

  useEffect(() => {
    verifyAdminAccess();
  }, []);

  const verifyAdminAccess = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        toast.error('Please log in first');
        navigate('/login');
        return;
      }

      const hasAccess = await checkAdminAccess(user.uid);
      if (!hasAccess) {
        toast.error('You do not have admin access');
        navigate('/dashboard');
        return;
      }

      console.log('Admin access verified');
    } catch (error) {
      console.error('Error verifying access:', error);
      toast.error('Error verifying permissions');
    } finally {
      setCheckingAccess(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const generateQuestions = () => {
    setGeneratingQuestions(true);
    
    // Generate questions based on job requirements
    const baseQuestions = [
      `Tell me about yourself and your experience relevant to the ${formData.positionTitle || 'this'} position.`,
      `What interests you about this ${formData.positionTitle || 'role'} at our company?`,
      `Can you describe your experience with ${formData.requiredSkills.split(',')[0]?.trim() || 'the required technologies'}?`,
      `Tell me about a challenging project you worked on and how you overcame obstacles.`,
      `How do you stay updated with the latest trends in ${formData.department || 'your field'}?`,
      `Describe a situation where you had to work in a team. What was your role?`,
      `What are your salary expectations for this role?`,
      `Where do you see yourself in 5 years?`
    ];

    setQuestions(baseQuestions);
    setShowQuestions(true);
    setGeneratingQuestions(false);
  };

  const handleAddQuestion = () => {
    setQuestions([...questions, '']);
  };

  const handleQuestionChange = (index, value) => {
    const newQuestions = [...questions];
    newQuestions[index] = value;
    setQuestions(newQuestions);
  };

  const handleDeleteQuestion = (index) => {
    const newQuestions = questions.filter((_, i) => i !== index);
    setQuestions(newQuestions);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!showQuestions) {
      // Generate questions first
      generateQuestions();
      return;
    }

    if (questions.length === 0) {
      toast.error('Please add at least one question');
      return;
    }

    setLoading(true);

    try {
      const user = auth.currentUser;
      if (!user) {
        toast.error('Please log in first');
        navigate('/login');
        return;
      }

      const interviewData = {
        ...formData,
        requiredSkills: formData.requiredSkills.split(',').map(s => s.trim()).filter(Boolean),
        questions: questions.filter(q => q.trim()), // Save custom questions
        status: 'active',
        createdAt: serverTimestamp(),
        createdBy: user.uid,
        candidates: []
      };

      console.log('Creating interview with data:', interviewData);
      const docRef = await addDoc(collection(db, 'interviews'), interviewData);
      console.log('Interview created with ID:', docRef.id);
      toast.success('Interview created successfully!');
      navigate(`/admin/interview-management/add-candidate/${docRef.id}`);
    } catch (error) {
      console.error('Error creating interview:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      let errorMessage = 'Failed to create interview';
      if (error.code === 'permission-denied') {
        errorMessage = 'Permission denied. Please ensure you have admin access and Firestore rules are deployed.';
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (checkingAccess) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Verifying permissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <button
        onClick={() => navigate('/admin/interview-management')}
        className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6"
      >
        <ArrowLeft className="w-5 h-5" />
        Back to Interviews
      </button>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
          Create New Interview
        </h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Position Title *
              </label>
              <input
                type="text"
                name="positionTitle"
                value={formData.positionTitle}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="e.g., Senior Software Engineer"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Department *
              </label>
              <input
                type="text"
                name="department"
                value={formData.department}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="e.g., Engineering"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Experience Level *
            </label>
            <select
              name="experienceLevel"
              value={formData.experienceLevel}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="">Select experience level</option>
              <option value="Entry Level">Entry Level (0-2 years)</option>
              <option value="Mid Level">Mid Level (2-5 years)</option>
              <option value="Senior Level">Senior Level (5-10 years)</option>
              <option value="Lead/Principal">Lead/Principal (10+ years)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Job Description *
            </label>
            <textarea
              name="jobDescription"
              value={formData.jobDescription}
              onChange={handleChange}
              required
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="Describe the role, responsibilities, and expectations..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Required Skills (comma-separated) *
            </label>
            <input
              type="text"
              name="requiredSkills"
              value={formData.requiredSkills}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="e.g., React, Node.js, MongoDB, AWS"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Technical Requirements
            </label>
            <textarea
              name="technicalRequirements"
              value={formData.technicalRequirements}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="Specific technical requirements and qualifications..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Soft Skills Required
            </label>
            <textarea
              name="softSkillsRequired"
              value={formData.softSkillsRequired}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="Communication, leadership, problem-solving..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Additional Notes
            </label>
            <textarea
              name="additionalNotes"
              value={formData.additionalNotes}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="Any other information for the AI interviewer..."
            />
          </div>

          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate('/admin/interview-management')}
              className="flex-1 px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || generatingQuestions}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Creating...
                </>
              ) : generatingQuestions ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Generating Questions...
                </>
              ) : showQuestions ? (
                'Create Interview'
              ) : (
                'Generate Questions & Continue'
              )}
            </button>
          </div>

          {/* Questions Section */}
          {showQuestions && (
            <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Interview Questions ({questions.length})
                </h2>
                <button
                  type="button"
                  onClick={handleAddQuestion}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                >
                  <Upload className="w-4 h-4" />
                  Add Question
                </button>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Review and customize the auto-generated questions. You can edit, delete, or add new questions.
              </p>

              <div className="space-y-4">
                {questions.map((question, index) => (
                  <div key={index} className="flex gap-3 items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 rounded-full flex items-center justify-center font-semibold text-sm">
                      {index + 1}
                    </div>
                    <textarea
                      value={question}
                      onChange={(e) => handleQuestionChange(index, e.target.value)}
                      rows={2}
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white resize-none"
                      placeholder="Enter your question..."
                    />
                    <button
                      type="button"
                      onClick={() => handleDeleteQuestion(index)}
                      className="flex-shrink-0 p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      title="Delete question"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>

              {questions.length === 0 && (
                <div className="text-center py-8 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <p className="text-gray-600 dark:text-gray-400">
                    No questions yet. Click "Add Question" to create one.
                  </p>
                </div>
              )}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
